import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Globe, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export interface LocalizedString {
  jp: string;
  en: string;
}

export interface BusinessService {
  id: string;
  title: LocalizedString;
  description: LocalizedString;
  icon: React.ElementType;
  link?: string;
  linkText?: LocalizedString;
}

export interface FounderMessage {
  name: LocalizedString;
  role: LocalizedString;
  content: LocalizedString;
  multicultural: LocalizedString;
}

export interface ComplianceInfo {
  title: LocalizedString;
  content: LocalizedString;
}

export function BusinessCard({ service }: { service: BusinessService }) {
  const { t } = useLanguage();
  const Icon = service.icon;

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="h-full"
    >
      <Card className="h-full flex flex-col border-border/50 bg-card shadow-sm hover:shadow-md transition-shadow">
        <CardHeader>
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 text-primary">
            <Icon size={24} />
          </div>
          <CardTitle className="text-xl font-bold text-foreground">
            {t(service.title)}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-grow">
          <p className="text-muted-foreground leading-relaxed">
            {t(service.description)}
          </p>
        </CardContent>
        {service.link && (
          <CardFooter>
            <Button
              variant="outline"
              className="w-full group border-primary/20 hover:border-primary hover:bg-primary/5 text-primary"
              asChild
            >
              <a href={service.link} target="_blank" rel="noopener noreferrer">
                {service.linkText ? t(service.linkText) : 'Details'}
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </Button>
          </CardFooter>
        )}
      </Card>
    </motion.div>
  );
}

export function FounderCard({ message }: { message: FounderMessage }) {
  const { t } = useLanguage();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-8 space-y-8">
        <div className="space-y-4">
          <p className="text-primary font-medium tracking-wider uppercase text-sm">
            {t(message.role)}
          </p>
          <h2 className="text-3xl font-bold text-foreground">
            {t(message.name)}
          </h2>
        </div>

        <div className="prose prose-slate max-w-none">
          <p className="text-lg leading-loose text-foreground/90 whitespace-pre-line">
            {t(message.content)}
          </p>
        </div>

        <div className="p-8 bg-secondary/30 rounded-xl border border-border/50 space-y-4">
          <div className="flex items-center gap-3 text-primary">
            <Globe size={20} />
            <span className="font-semibold">
              {t({ jp: '多文化背景とグローバルな視点', en: 'Multicultural Background & Global Perspective' })}
            </span>
          </div>
          <p className="text-muted-foreground leading-relaxed italic">
            {t(message.multicultural)}
          </p>
        </div>
      </div>

      <div className="lg:col-span-4 space-y-6">
        <div className="aspect-[4/5] rounded-2xl bg-muted overflow-hidden border border-border/40 shadow-inner flex items-center justify-center text-muted-foreground">
          {/* Placeholder for professional portrait */}
          <div className="text-center p-6">
            <p className="text-sm">Corporate Portrait</p>
            <p className="text-xs mt-1 opacity-60">Qin Jialing (秦 佳玲)</p>
          </div>
        </div>
        <div className="flex flex-col gap-2 p-4 border-l-2 border-primary/30">
          <span className="text-xs text-muted-foreground uppercase tracking-widest">Market Expertise</span>
          <span className="text-sm font-medium">Japan • China • Global English Markets</span>
        </div>
      </div>
    </div>
  );
}

export function ComplianceCard({ info }: { info: ComplianceInfo }) {
  const { t } = useLanguage();

  return (
    <div className="group p-6 bg-background border border-border/60 rounded-lg hover:border-primary/40 transition-colors shadow-sm">
      <div className="flex items-start gap-4">
        <div className="mt-1 text-primary">
          <ShieldCheck size={24} className="opacity-80 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
            {t(info.title)}
            <CheckCircle2 size={16} className="text-primary/40" />
          </h3>
          <p className="text-muted-foreground leading-relaxed">
            {t(info.content)}
          </p>
        </div>
      </div>
    </div>
  );
}
